import './ViewRun.css';
import './ViewControls.css';

import _ from 'lodash';
import logger from 'loglevel';
import { FunctionComponent, useEffect, useMemo, useRef, Suspense } from 'react';
import {
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  MeshBasicMaterial,
  Mesh,
  DoubleSide,
  Quaternion,
  ShaderMaterial,
  BackSide,
  Color,
  Clock,
  TextureLoader,
  RepeatWrapping,
  SRGBColorSpace,
} from 'three';

import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import SkateboardModel from 'models/RunModel/Skateboard';
import { RunModel } from 'models/RunModel/RunModel';
import ControlsOverlay from './ControlsOverlay';
import SkateparkScene from './SkateparkScene';
import Skateboard from './SkateparkScene/Skateboard';
import ViewControls from './ViewControls';
import {
  PerformanceMonitor,
  PerformanceMonitorApi,
} from './PerformanceMonitor';
import { useStores } from 'models';
import { observer } from 'mobx-react-lite';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader';

logger.setLevel(0);

interface ViewRunProps {
  record?: RunModel;
  children?: React.ReactNode | React.ReactNode[];
  hidden?: boolean;
  hasVideo: boolean;
  videoActive: boolean;
  onVideoToggle: () => void;
  showRunStatsBox?: boolean;
  showFigurePopup?: boolean;
  showGForceDisplay?: boolean;
}

// Globaler State für Animation Resume (überlebt Hot Reloads)
let globalAnimationState = {
  isFirstPlay: true,
  resumeStartTime: 0,
  resumeFromFrame: 0
};

const skateboardStore = new SkateboardModel();

// GLSL Shader aus dem three.js Beispiel (leicht angepasst für R3F)
const skyVertexShader = `
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * vec4(position, 1.0);
  }
`;

const skyFragmentShader = `
  void main() {
    gl_FragColor = vec4(0.5, 0.7, 1.0, 1.0);
  }
`;

function SkyShader() {
  return (
    <mesh scale={500} position={[0, 0, 0]} renderOrder={999} frustumCulled={false}>
      <sphereGeometry args={[1, 32, 15]} />
      <meshBasicMaterial color={0x87ceeb} side={DoubleSide} />
    </mesh>
  );
}

function SkySphereContent({ textureUrl }: { textureUrl: string }) {
  const texture = useLoader(TextureLoader, textureUrl);
  useEffect(() => {
    if (texture) {
      texture.colorSpace = SRGBColorSpace;
      console.log("Sky-Textur geladen:", textureUrl, texture);
    } else {
      console.warn("Sky-Textur NICHT geladen:", textureUrl);
    }
  }, [texture, textureUrl]);
  return (
    <>
      {/* Eigentliche SkySphere */}
      <mesh scale={500} position={[0, 0, 0]} frustumCulled={false} renderOrder={-1000}>
        <sphereGeometry args={[1, 64, 32]} />
        <meshBasicMaterial map={texture} side={BackSide} />
      </mesh>
    </>
  );
}

function SkySphere({ textureUrl = "/assets/3d/sky.jpg" }: { textureUrl?: string }) {
  return (
    <Suspense fallback={<SkyShader />}>
      <SkySphereContent textureUrl={textureUrl} />
    </Suspense>
  );
}

function SkySphereEXR({ exrUrl = "/assets/3d/autumn_field_puresky_4k.exr" }: { exrUrl?: string }) {
  const texture = useLoader(EXRLoader, exrUrl);
  return (
    <mesh
      scale={50}
      position={[0, 0, 0]}
      frustumCulled={false}
      renderOrder={-1000}
    >
      <sphereGeometry args={[1, 64, 32]} />
      <meshBasicMaterial map={texture} side={DoubleSide} />
    </mesh>
  );
}

// Komponente für ein Schachbrettmuster-Mesh, das von der Trajektorie "angehoben" wird
function GroundMeshChess({ obs, showDebug = false, textureUrl }: { obs: SkateboardModel, showDebug?: boolean, textureUrl?: string }) {
  // useLoader immer ganz oben, damit kein Hook-Fehler entsteht
  const texture = useLoader(TextureLoader, textureUrl || '/assets/3d/skateground.jpg');
  // Textur wiederholen für höhere Auflösung
  texture.wrapS = texture.wrapT = RepeatWrapping;
  texture.repeat.set(1, 1); // 10x10 Wiederholung, ggf. anpassen
  // Dynamische Mesh-Größe: Grid und Radius abhängig von Trajektorie
  const trajPoints = obs.allPosData
    .filter((d) => d && d.loc && typeof d.loc.x === 'number' && typeof d.loc.y === 'number' && typeof d.loc.z === 'number')
    .map((d) => d.loc);
  if (trajPoints.length < 2) return null;
  const center = trajPoints.reduce((acc, v) => acc.add(v), new Vector3()).divideScalar(trajPoints.length);
  // Maximaler Abstand eines Trajektoriepunkts zum Mittelpunkt
  let maxDist = 0;
  for (const p of trajPoints) {
    const dist = Math.sqrt((p.x - center.x) ** 2 + (p.z - center.z) ** 2);
    if (dist > maxDist) maxDist = dist;
  }
  // Großzügiger Puffer (z.B. 20 Meter)
  const safeRadius = maxDist + 20;
  // Grid-Auflösung: ca. 0.16m pro Feld, mindestens 260x260, abhängig von safeRadius
  const gridSize = Math.max(260, Math.ceil((safeRadius * 2) / 0.16));
  const spacing = (safeRadius * 2) / gridSize;
  const minY = Math.min(...trajPoints.map((v) => v.y));
  // Kreisradius für "runden" Rand
  const radius = safeRadius;
  function closestTrajY(x: number, z: number) {
    let minDist = Infinity;
    let y = minY;
    for (const p of trajPoints) {
      const dx = x - p.x;
      const dz = z - p.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < minDist) {
        minDist = dist;
        y = p.y;
      }
    }
    return { minDist, y };
  }
  const points: Array<{x:number, y:number, z:number}> = [];
  for (let ix = 0; ix < gridSize; ix++) {
    for (let iz = 0; iz < gridSize; iz++) {
      const x = center.x + (ix - gridSize / 2) * spacing;
      const z = center.z + (iz - gridSize / 2) * spacing;
      const { minDist, y: trajY } = closestTrajY(x, z);
      // Abstand zum Mittelpunkt für runden Rand
      const distToCenter = Math.sqrt((x - center.x) ** 2 + (z - center.z) ** 2);
      // Kreisabfall: ab 90% des Radius absenken, am Rand stark abfallen
      let rim = 0;
      if (distToCenter > radius * 0.9) {
        rim = Math.pow((distToCenter - radius * 0.9) / (radius * 0.1), 2) * 1.2; // starker Drop
      }
      // Anhebung: bis 0.25m, Einflussbereich bis 2.5m
      const lift = Math.max(0, 0.25 * (1 - Math.min(minDist, 2.5) / 2.5));
      let y = minY + (trajY - minY) * lift + lift - rim;
      // Verstärkte Wellen außerhalb der Trajektorie (ab 1.2*radius)
      if (distToCenter > radius * 1.2) {
        const freq = 0.28 + 0.18 * Math.sin(x * 0.13);
        const amp = 0.55 + 0.25 * Math.cos(z * 0.17);
        y += Math.sin(x * freq + z * freq * 0.7) * amp;
      } else if (distToCenter > radius) {
        // Übergangsbereich: Wellen sanft einblenden
        const t = (distToCenter - radius) / (radius * 0.2);
        const freq = 0.28 + 0.18 * Math.sin(x * 0.13);
        const amp = 0.55 + 0.25 * Math.cos(z * 0.17);
        y += Math.sin(x * freq + z * freq * 0.7) * amp * t;
      }
      points.push({ x, y, z });
    }
  }
  const vertices: number[] = [];
  const indices: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  for (let ix = 0; ix < gridSize; ix++) {
    for (let iz = 0; iz < gridSize; iz++) { // <-- Schleifenbedingung korrigiert
      const idx = ix * gridSize + iz;
      vertices.push(points[idx].x, points[idx].y, points[idx].z);
      uvs.push(ix / (gridSize - 1), iz / (gridSize - 1));
      // Farben für Schachbrettmuster
      const isWhite = ((ix + iz) % 2 === 0);
      const c = isWhite ? 0.92 : 0.68;
      colors.push(c, c, c);
    }
  }
  for (let ix = 0; ix < gridSize - 1; ix++) {
    for (let iz = 0; iz < gridSize - 1; iz++) {
      const a = ix * gridSize + iz;
      const b = (ix + 1) * gridSize + iz;
      const c = (ix + 1) * gridSize + (iz + 1);
      const d = ix * gridSize + (iz + 1);
      if ((ix + iz) % 2 === 0) {
        indices.push(a, b, d, b, c, d);
      } else {
        indices.push(a, b, c, a, c, d);
      }
    }
  }
  // Geometrie
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  let material: MeshBasicMaterial;
  if (textureUrl) {
    material = new MeshBasicMaterial({ map: texture, side: DoubleSide });
  } else {
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    material = new MeshBasicMaterial({ vertexColors: true, side: DoubleSide });
  }
  let debugSpheres = null;
  if (showDebug) {
    debugSpheres = trajPoints.map((d: Vector3, i: number) => (
      <mesh key={i} position={[d.x, d.y, d.z]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color={0xff0000} />
      </mesh>
    ));
  }
  return (
    <group position={[0, -0.2, 0]}>
      <mesh geometry={geometry} material={material} />
      {debugSpheres}
    </group>
  );
}

const ViewRun: FunctionComponent<ViewRunProps> = observer(
  ({
    record,
    children,
    hidden = false,
    hasVideo,
    videoActive,
    onVideoToggle,
    showRunStatsBox = true,
    showFigurePopup = false,
    showGForceDisplay = false,
  }) => {
    const { appData } = useStores();
    const { threeSettings, displaySettings } = appData;

    const clock = useMemo(() => new Clock(false), []);
    const initializationRef = useRef<string | null>(null);
    const orbitControlsRef = useRef<OrbitControlsImpl | null>(null);

    useEffect(() => {
      if (!record?.playData) {
        return;
      }
      
      const recordId = record.id;
      if (initializationRef.current === recordId) {
        return;
      }
      initializationRef.current = recordId;
      
      // Add a small delay to prevent race conditions
      const timeoutId = setTimeout(() => {
        if (initializationRef.current !== recordId) {
          return;
        }
      
      skateboardStore.setFrom(record?.playData);
      skateboardStore.reset(); // Animation garantiert pausiert und am Anfang
      clock.stop();
      
      // Initial soll playing sein, nicht paused
      skateboardStore.play();
      
      // Interval für Animation setzen
      if (skateboardStore.timeStamps && skateboardStore.timeStamps.length > 1) {
        const avgInterval = (skateboardStore.timeStamps[skateboardStore.timeStamps.length - 1] - skateboardStore.timeStamps[0]) / (skateboardStore.timeStamps.length - 1);
        skateboardStore.setInterval(avgInterval > 0 ? avgInterval : 16);
      } else {
        skateboardStore.setInterval(16);
      }
      
      // Clock für Auto-Start initialisieren
      clock.elapsedTime = 0;
      
      // Initial Play Event für FrameSync senden
      const initialPlayEvent = new CustomEvent('animationResume', { 
        detail: { frame: 0, time: 0 } 
      });
      window.dispatchEvent(initialPlayEvent);
      
      clock.start();
      
      skateboardStore.logState("after setFrom+reset+play+clockStart");
      }, 100); // 100ms delay to prevent race conditions
      
      return () => clearTimeout(timeoutId);
    }, [record?.playData, clock, record?.id]);

    // Reset initialization when record changes
    useEffect(() => {
      return () => {
        // Cleanup when component unmounts or record changes
        if (initializationRef.current !== record?.id) {
          initializationRef.current = null;
        }
      };
    }, [record?.id]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        // Reset any global state if needed
        initializationRef.current = null;
      };
    }, []);

    // Komponente, die den Frame im Canvas-Kontext synchronisiert
    const FrameSync: React.FC<{ store: SkateboardModel; clock: Clock }> = ({ store, clock }) => {
      // Custom Event Listener für synchrones Resume-Setup
      useEffect(() => {
        const handleResume = (event: CustomEvent) => {
          const { frame, time } = event.detail;
          if (globalAnimationState.isFirstPlay) {
            // Erstes Play nach Reset/Load
            globalAnimationState.isFirstPlay = false;
          } else {
            // Resume von pausierter Position
            globalAnimationState.resumeStartTime = time;
            globalAnimationState.resumeFromFrame = frame;
          }
        };
        
        window.addEventListener('animationResume', handleResume as EventListener);
        return () => window.removeEventListener('animationResume', handleResume as EventListener);
      }, []);
      
      useFrame(() => {
        // Animation pausiert oder am Ende? Dann KEIN Frame-Update!
        if (store.animationState !== "playing" || store.positionState === "end") {
          return;
        }
        
        // Zeit seit Start in ms
        const elapsed = clock.getElapsedTime() * 1000;
        
        // Berechne die aktuelle Zeit in der Animation
        let effectiveTime: number;
        if (globalAnimationState.isFirstPlay) {
          // Erstes Play: normal von 0 starten
          effectiveTime = elapsed;
        } else {
          // Resume: von der pausierten Frame-Zeit + vergangene Zeit seit Resume
          const timeSinceResume = elapsed - globalAnimationState.resumeStartTime;
          const resumeFrameTime = store.timeStamps[globalAnimationState.resumeFromFrame] || 0;
          effectiveTime = resumeFrameTime + timeSinceResume;
        }
        
        // Finde den passenden Frame zur effektiven Zeit
        let frameIdx = 0;
        for (let i = 0; i < store.timeStamps.length; i++) {
          if (store.timeStamps[i] > effectiveTime) break;
          frameIdx = i;
        }
        
        // Stoppe Animation, wenn letzter Frame erreicht
        if (frameIdx >= store.frameNum - 1) {
          clock.stop();
          store.setInterval(0);
          store.end(); // State-Machine: Animation am Ende
          return;
        }
        
        // ✅ FIX: Frame-Update NUR wenn Animation läuft UND sich der Frame ändert
        if (store.animationState === "playing" && store.frame !== frameIdx) {
          store.frame = frameIdx;
        }
      });
      
      // ✅ FIX: Bei Reset: Flags zurücksetzen - nur basierend auf store state, nicht auf globalAnimationState
      useEffect(() => {
        if (store.positionState === "start" && store.frame === 0) {
          // Reset nur wenn nicht bereits im "first play" Zustand
          if (!globalAnimationState.isFirstPlay) {
            globalAnimationState.isFirstPlay = true;
            globalAnimationState.resumeStartTime = 0;
            globalAnimationState.resumeFromFrame = 0;
          }
        }
      }, [store.positionState, store.frame]); // ✅ FIX: Removed globalAnimationState.isFirstPlay dependency
      
      return null;
    };

    // Komponente für ein Band entlang der Skateboard-Achse
    const SkateboardRibbon = observer(({ obs }: { obs: SkateboardModel }) => {
      const width = 0.2;
      // GANZE Trajektorie anzeigen
      const leftPoints: Vector3[] = [];
      const rightPoints: Vector3[] = [];
      // Geschwindigkeit für jeden Abschnitt berechnen
      const speeds: number[] = [];
      let maxSpeed = 0;
      if (obs?.allPosData && Array.isArray(obs.allPosData)) {
        const q90 = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          Math.PI / 2
        );
        for (let i = 0; i < obs.allPosData.length; i++) {
          const d = obs.allPosData[i];
          if (!d) continue;
          const rot90 = d.rot.clone().multiply(q90);
          const left = new Vector3(-width / 2, 0, 0)
            .applyQuaternion(rot90)
            .add(d.loc);
          const right = new Vector3(width / 2, 0, 0)
            .applyQuaternion(rot90)
            .add(d.loc);
          leftPoints.push(left);
          rightPoints.push(right);
          // Geschwindigkeit für Farbe berechnen
          let speed = 0;
          if (i > 0 && obs.allPosData[i - 1]) {
            const p1 = d.loc;
            const p0 = obs.allPosData[i - 1].loc;
            const dt = obs.timeStamps[i] - obs.timeStamps[i - 1];
            if (dt > 0) {
              speed = p1.distanceTo(p0) / (dt / 1000);
            }
          }
          speeds.push(speed);
          if (speed > maxSpeed) maxSpeed = speed;
        }
      }
      if (leftPoints.length < 2 || rightPoints.length < 2) return null;
      // Geometrie für das Band (als Rechteck-Strip, keine Lücken)
      const positions: number[] = [];
      const colorArr: number[] = [];
      for (let i = 0; i < leftPoints.length - 1; i++) {
        // Vier Punkte für das Segment
        const l0 = leftPoints[i];
        const r0 = rightPoints[i];
        const l1 = leftPoints[i + 1];
        const r1 = rightPoints[i + 1];
        // Farben für die vier Punkte
        let t0 = 0;
        let t1 = 0;
        if (maxSpeed > 0) {
          t0 = Math.min(1, speeds[i] / maxSpeed);
          t1 = Math.min(1, speeds[i + 1] / maxSpeed);
        }
        const minBlue = 0.2;
        const color0 = [t0, 0, (1 - t0) * (1 - minBlue) + minBlue];
        const color1 = [t1, 0, (1 - t1) * (1 - minBlue) + minBlue];
        // Dreieck 1: l0, r0, l1
        positions.push(l0.x, l0.y, l0.z);
        positions.push(r0.x, r0.y, r0.z);
        positions.push(l1.x, l1.y, l1.z);
        colorArr.push(...color0, ...color0, ...color1);
        // Dreieck 2: r0, r1, l1
        positions.push(r0.x, r0.y, r0.z);
        positions.push(r1.x, r1.y, r1.z);
        positions.push(l1.x, l1.y, l1.z);
        colorArr.push(...color0, ...color1, ...color1);
      }
      const geometry = new BufferGeometry();
      geometry.setAttribute(
        'position',
        new Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute('color', new Float32BufferAttribute(colorArr, 3));
      // Material mit VertexColors
      const material = new MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.3,
        side: DoubleSide,
      });
      return <primitive object={new Mesh(geometry, material)} />;
    });

    const performanceInclined = (_: PerformanceMonitorApi) => {
      if (threeSettings.dpr === window.devicePixelRatio) return;
      const newDpr = Math.min(window.devicePixelRatio, threeSettings.dpr + 0.1);
      threeSettings.setDpr(newDpr);
      if (newDpr === window.devicePixelRatio) {
        threeSettings.setCastShadow(true);
      }
    };
    const performanceDeclined = (_: PerformanceMonitorApi) => {
      if (threeSettings.dpr === 1) return;
      const newDpr = Math.max(1, threeSettings.dpr - 0.1);
      threeSettings.setDpr(newDpr);
      if (newDpr === 1) {
        threeSettings.setCastShadow(false);
      }
    };

    return (
      <div
        id="dataplayer"
        style={{
          display: hidden ? 'none' : undefined,
          background: 'transparent',
          position: 'relative',
          height: '100%',
        }}
      >
        <Canvas
          dpr={threeSettings.dpr}
          shadows
          camera={{ fov: 45, far: 100, near: 0.1, position: [5, 5, 5] }}
          id="playercanvas"
          onCreated={({ gl }) => {
            gl.domElement.addEventListener('webglcontextlost', (event) => {
              event.preventDefault();
            });
            gl.domElement.addEventListener('webglcontextrestored', () => {});
            gl.outputColorSpace = SRGBColorSpace;
          }}
        >
          <SkySphereEXR exrUrl="/assets/3d/autumn_field_puresky_4k.exr" />
          {/* <SkySphere textureUrl="/assets/3d/sky.jpg" /> */}
          {/* <SkyShader /> */}
          {/* Lichtquellen für Skateboard und Szene */}
          <ambientLight intensity={0.7} />
          <directionalLight position={[10, 20, 10]} intensity={0.5} castShadow />
          {/* <PerformanceMonitor
            onDecline={performanceDeclined}
            onIncline={performanceInclined}
            bounds={(_refreshRate) => {
              return [30, 60];
            }}
          /> */}
          <FrameSync store={skateboardStore} clock={clock} />
          {/* Ground rendering: entweder Plain oder Mesh, nie beides */}
          {displaySettings.showGround && !displaySettings.showGroundMesh && (
            <SkateparkScene castShadow={threeSettings.castShadow} showGround={true} />
          )}
          {displaySettings.showGroundMesh && (
            <GroundMeshChess obs={skateboardStore} showDebug={false} textureUrl={"/assets/3d/skateground.jpg"} />
          )}
          {displaySettings.showTrajectory && <SkateboardRibbon obs={skateboardStore} />}
          <Skateboard obs={skateboardStore} controlsRef={orbitControlsRef} />
        </Canvas>
        <div>
          {children}
          <ViewControls controlsRef={orbitControlsRef} />
          <ControlsOverlay
            record={record!}
            clock={clock}
            store={skateboardStore}
            hasVideo={hasVideo}
            videoActive={videoActive}
            onVideoToggle={onVideoToggle}
            showRunStatsBox={showRunStatsBox}
            showFigurePopup={showFigurePopup}
            showGForceDisplay={showGForceDisplay}
          />
        </div>
      </div>
    );
  }
);

export default ViewRun;
